import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Form } from 'semantic-ui-react'
import SurveyService from '~/api/services/survey'
import Layout from '~/components/Layout'
import { StyledFlag } from '~/components/TopBar/style'
import { useAuth } from '~/routes/components/AuthProvider'
import { RouteTypes } from '~/routes/constants'
import { AnswerWrapper, StyledAnswerWrapper, StyledFlagName, StyledForm } from './style'
import { MaxValueLabel, MinValueLabel, SliderC, SliderWrapper } from '../CreateEditQuestion/QuestionForms/Slider'
import { StyledRating } from '../CreateEditQuestion/QuestionForms/Stars'
import { ReactSortable } from 'react-sortablejs'
import { formatNumber, formatNumberOneDecimal, formatNumberWithoutFractions, getFlagSrc, handleErrors } from '~/utils/helpers'
import { StyledGroupQuestion } from '../CreateEditQuestion/style'

const ShowQuestion: React.FC = () => {
  const { t } = useTranslation()
  const [isBusy, setIsBusy] = useState(false)
  const [question, setQuestion] = useState(null)
  const { id, idQuestion } = useParams()
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const [answerSortingList, setAnswerSortingList] = useState(languages.reduce((acc, lang) => {
    acc[lang.code] = [];
    return acc;
  }, {}))
  const [rating, setRating] = useState(languages.reduce((acc, lang) => {
    acc[lang.code] = 1;
    return acc;
  }, {}))

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setIsBusy(true);
    try {
      const response = await SurveyService.showSurveyQuestion(id, idQuestion)
      if (response.data?.type === 'sorting') {
        const answersSorting = {}
        response.data?.languages.map(languageA => {
          answersSorting[languageA.code] = response.data?.answers?.map((answer) =>
            answer.languages.find((language) => language.id === languageA.id)?.answer ?? ''
          );
        })
        setAnswerSortingList(answersSorting)
      }
      setQuestion(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  const handleChange = (event, code) => {
    setRating({
      ...rating,
      [code]: event.target.value
    });
  };

  function renderSortItems(answers) {
    return answers.map((answer, index) => {
      return <AnswerWrapper key={index}>
        {`${index + 1}. ${answer}`}
      </AnswerWrapper>
    })
  }

  function formatNumbersBasedOnType(question) {
    const { minValue, maxValue } = question
    switch (question.subtype) {
      case 'whole_number':
        return { minValue: parseInt(minValue), maxValue: parseInt(maxValue) }
      case 'one_decimal_number':
        return { minValue: formatNumberOneDecimal(minValue), maxValue: formatNumberOneDecimal(maxValue) }
      case 'two_decimals_number':
        return { minValue: formatNumber(minValue), maxValue: formatNumber(maxValue) }
      default:
        return { minValue: 0, maxValue: 0 }
    }
  }

  function getNumberLabel(languageAnswer, question) {
    const { minValue, maxValue } = formatNumbersBasedOnType(question)

    return `${languageAnswer?.question} (${minValue} ${languageAnswer?.unitLabel || ''} - ${maxValue} ${languageAnswer?.unitLabel || ''})`
  }

  function renderAnwserBasedOnType(languageAnswer, multipleAnswers = []) {
    switch (question?.type) {
      case 'text':
        return <Form.Input
          disabled
          required={Boolean(question?.required)}
          label={languageAnswer?.question}
          placeholder={languageAnswer?.placeholder}
        />
      case 'radio':
        return <>
          <Form.Field label={languageAnswer?.question} required={question?.required} />
          {multipleAnswers.map((answer) => {
            return <Form.Checkbox
              label={answer}
              radio
              key={Math.random()}
            />
          })}
        </>
      case 'checkbox':
        return <>
          <Form.Field label={languageAnswer?.question} required={question?.required} />
          {multipleAnswers.map((answer) => {
            return <Form.Checkbox
              label={answer}
              key={Math.random()}
            />
          })}
        </>
      case 'slider':
        return <div key={languageAnswer?.id}>
          <Form.Field label={languageAnswer?.question} required={Boolean(question?.required)} />
          <Form.Field label={t('Rating: {{rating}} {{watchUnit}}',
            { rating: formatNumberWithoutFractions(rating[languageAnswer?.code] || 0), watchUnit: languageAnswer?.unitLabel || '' })} />
          <SliderWrapper>
            <MinValueLabel>{`${formatNumberWithoutFractions(question?.minValue)} ${languageAnswer?.unitLabel || ''}`}</MinValueLabel>
            <SliderC
              step={parseInt(question?.interval)}
              rating={((parseInt(rating[languageAnswer?.code]) - question?.minValue) / (question?.maxValue - question?.minValue)) * 100}
              min={question?.minValue}
              max={question?.maxValue}
              value={parseInt(rating[languageAnswer?.code])}
              onChange={(e) => handleChange(e, languageAnswer?.code)}
            />
            <MaxValueLabel>{`${formatNumberWithoutFractions(question?.maxValue)} ${languageAnswer?.unitLabel || ''}`}</MaxValueLabel>
          </SliderWrapper>
        </div>
      case 'stars':
        return <>
          <Form.Field label={languageAnswer?.question} required={Boolean(question?.required)} />
          <StyledRating
            icon='star'
            defaultRating={0}
            maxRating={question?.value}
            size={'large'}
          />
        </>
      case 'multiselect':
        return <Form.Select
          label={languageAnswer?.question}
          required={Boolean(question?.required)}
          multiple
          placeholder={languageAnswer?.placeholder}
          options={getAnswersByLanguage(languageAnswer?.id).map(anwser => ({ text: anwser, value: anwser }))}
        />
      case 'sorting':
        return <>
          <Form.Field label={languageAnswer?.question} required={Boolean(question?.required)} />
          <ReactSortable
            list={answerSortingList[languageAnswer?.code]}
            animation={150}
            delayOnTouchOnly={true}
            delay={2}
            setList={(newState) => {
              setAnswerSortingList({
                ...answerSortingList,
                [languageAnswer?.code]: newState
              })
            }}
          >
            {renderSortItems(answerSortingList[languageAnswer?.code])}
          </ReactSortable>
        </>
      case 'multitext':
        return <>
          <Form.Field label={languageAnswer?.question} required={Boolean(question?.required)} />
          {Array.from({ length: question?.value }, (_, index) => index).map((anwser) => {
            return <Form.TextArea
              key={anwser}
              placeholder={languageAnswer?.placeholder}
            />
          })}
        </>
      case 'select':
        return <Form.Select
          label={languageAnswer?.question}
          required={Boolean(question?.required)}
          placeholder={languageAnswer?.placeholder}
          options={getAnswersByLanguage(languageAnswer?.id).map(anwser => ({ text: anwser, value: anwser }))}
        />
      case 'number':
        return <Form.Input
          placeholder={languageAnswer?.placeholder}
          label={<div style={{ display: 'flex' }}>
            <div style={{ marginBottom: '1em' }}>
              {languageAnswer && question && getNumberLabel(languageAnswer, question)}
            </div>
            {Boolean(question?.required) && <span style={{ color: 'red' }}>*</span>}
          </div>}
        />
      case 'group':
        return <>
          <Form.Field label={languageAnswer?.question} required={Boolean(question?.required)} />
          {question.groupQuestions?.map((groupQuestion) => {
            const langQuestion = groupQuestion?.languages?.find(questionLang => questionLang.languageId === languageId)
            return <div key={langQuestion.id} style={{ display: 'flex' }}>
              <StyledGroupQuestion
                label={langQuestion.question}
              />
              <Form.Group widths={'equal'}>
                {question?.answers?.map((answer) => {
                  const langAnswer = answer.languages.find(answerLang => answerLang.languageId === languageId)
                  return <Form.Field key={answer?.id} style={{ textAlign: 'center' }}>
                    <div className='radio-container'>
                      <label>{langAnswer.answer}</label>
                      <Form.Checkbox
                        radio
                        style={{ marginTop: 3 }}
                      />
                    </div>
                  </Form.Field>
                })}
              </Form.Group>
            </div>
          })}
        </>
      default:
        return <>
        </>
    }
  }

  function getAnswersByLanguage(languageId) {
    return question?.answers?.map((answer) =>
      answer.languages.find((language) => language.id === languageId)?.answer ?? ''
    );
  }

  function renderAnswerValues() {
    return question?.languages.map(languageAnswer => {
      const multipleAnswers = question?.answers?.length > 0 ? getAnswersByLanguage(languageAnswer?.id) : []
      return <StyledAnswerWrapper key={languageAnswer.code}>
        <StyledFlagName>
          <StyledFlag src={getFlagSrc(languageAnswer.icon)} />
          <div className='title'>{languageAnswer.label}:</div>
        </StyledFlagName>
        {renderAnwserBasedOnType(languageAnswer, multipleAnswers)}
      </StyledAnswerWrapper>
    })
  }

  function renderShowData() {
    const questionOnLoggedLanguage = question?.languages.find(language => language.id === languageId)
    return <>
      <StyledForm>
        <Form.Group widths={'equal'} style={{ marginBottom: '3em' }}>
          <Form.Field>
            <label>{t('Question')}</label>
            <div>{questionOnLoggedLanguage?.question}</div>
          </Form.Field>
          <Form.Field>
            <label>{t('Answer type')}</label>
            <div>{question?.answerType}</div>
          </Form.Field>
        </Form.Group>
        <Form.Field>
          <label>{t('Answer values')}:</label>
          <div>{renderAnswerValues()}</div>
        </Form.Field>
      </StyledForm>
    </>
  }

  return <Layout
    title={t('Question')}
    isBusy={isBusy}
    segmented
    close={RouteTypes.SURVEY_SHOW.replace(':id', id)}
  >
    {question && renderShowData()}
  </Layout>
}
export default ShowQuestion